/**
 * aeterna/neural/hdc.ts — Hyperdimensional Computing / VSA
 *
 * The room state IS a hypervector.
 *
 * Every concept in the Aeterna's world — furniture types, time slots,
 * mood dimensions, player behaviors — is represented as a 10,000-dim
 * binary vector. These are composed through binding (XOR) and bundling
 * (majority vote) into holographic scene representations.
 *
 * The Aeterna doesn't query a database. It XORs the scene with a concept
 * vector and measures Hamming distance. If the result is close to a known
 * pattern, the concept is present. This is algebraic reasoning over meaning.
 *
 * From PLAN.md: "The Aeterna can ask: 'Is this room currently associated
 * with solitude?' and get a number. No inference call. No lookup table.
 * Emergent semantic awareness from geometry."
 */

import { randomBinary, xor, bundle, permute, hamming } from "./math.js";

// ─── Config ──────────────────────────────────────────────────────────────────

/** Dimensionality of hypervectors. 10000 is the standard for binary VSA. */
const D = 10000;

/** Similarity threshold: normalized Hamming < this means "present" */
const SIMILARITY_THRESHOLD = 0.42;

// ─── Codebook ────────────────────────────────────────────────────────────────
// Every concept gets a random seed → deterministic binary HV.
// The codebook is built lazily and cached.

const codebook = new Map<string, Uint8Array>();
let nextSeed = 1337;

/** Get or create the hypervector for a named concept */
export function getHV(concept: string): Uint8Array {
  let hv = codebook.get(concept);
  if (!hv) {
    hv = randomBinary(D, nextSeed++);
    codebook.set(concept, hv);
  }
  return hv;
}

/** Pre-register a batch of concepts (call at init for known vocabularies) */
export function registerConcepts(concepts: string[]): void {
  for (const c of concepts) getHV(c);
}

// ─── Operations ──────────────────────────────────────────────────────────────

/** Bind two concepts: encodes "this goes with that" (XOR) */
export function bind(a: Uint8Array, b: Uint8Array): Uint8Array {
  return xor(a, b);
}

/** Bundle multiple HVs: encodes "all of these together" (majority vote) */
export function bundleHVs(hvs: Uint8Array[]): Uint8Array {
  return bundle(hvs);
}

/** Sequence-encode: permute then bind, encoding ordered associations */
export function sequenceBind(hvs: Uint8Array[]): Uint8Array {
  if (hvs.length === 0) return new Uint8Array(D);
  let result = permute(hvs[0], 0);
  for (let i = 1; i < hvs.length; i++) {
    result = xor(result, permute(hvs[i], i));
  }
  return result;
}

/** Normalized similarity: 0 = orthogonal, 1 = identical */
export function similarity(a: Uint8Array, b: Uint8Array): number {
  return 1 - hamming(a, b) / D;
}

/** Query: unbind a concept from a composite, return what's left */
export function query(composite: Uint8Array, concept: Uint8Array): Uint8Array {
  return xor(composite, concept);
}

/** Check if a concept is present in a composite scene */
export function isPresent(composite: Uint8Array, concept: string): boolean {
  const answer = query(composite, getHV(concept));
  // Check similarity against all known concepts
  const hv = getHV(concept);
  return similarity(xor(composite, hv), new Uint8Array(D)) > SIMILARITY_THRESHOLD;
}

// ─── Room State Encoder ──────────────────────────────────────────────────────

export interface RoomContext {
  furnitureTypes: string[];
  timeSlot: string;          // "morning" | "evening" | etc.
  season: string;
  warmth: number;            // 0–1
  energy: number;            // 0–1
  mystery: number;           // 0–1
  nature: number;            // 0–1
  noise: number;             // 0–1
  recentActivities: string[];
  playerMood?: string;       // optional mood label
  visitors: number;          // how many visitors recently
}

/** Quantize a 0–1 value into a named bucket for HDC encoding */
function quantize(name: string, value: number, buckets = 5): string {
  const bucket = Math.min(buckets - 1, Math.floor(value * buckets));
  return `${name}_${bucket}`;
}

/**
 * Encode a full room context into a single hypervector.
 *
 * This is the holographic representation the Aeterna reasons from.
 * Every aspect of the room — objects, atmosphere, time, recent events —
 * bound and bundled into one 10k-dim vector.
 */
export function encodeRoomState(ctx: RoomContext): Uint8Array {
  const components: Uint8Array[] = [];

  // Furniture: bind each type with the FURNITURE role vector
  const furnitureRole = getHV("ROLE_FURNITURE");
  for (const ft of ctx.furnitureTypes) {
    components.push(bind(furnitureRole, getHV(`furniture:${ft}`)));
  }

  // Time and season
  components.push(bind(getHV("ROLE_TIME"), getHV(`time:${ctx.timeSlot}`)));
  components.push(bind(getHV("ROLE_SEASON"), getHV(`season:${ctx.season}`)));

  // Mood dimensions (quantized)
  components.push(bind(getHV("ROLE_MOOD"), getHV(quantize("warmth", ctx.warmth))));
  components.push(bind(getHV("ROLE_MOOD"), getHV(quantize("energy", ctx.energy))));
  components.push(bind(getHV("ROLE_MOOD"), getHV(quantize("mystery", ctx.mystery))));
  components.push(bind(getHV("ROLE_MOOD"), getHV(quantize("nature", ctx.nature))));
  components.push(bind(getHV("ROLE_MOOD"), getHV(quantize("noise", ctx.noise))));

  // Recent activities (sequence-ordered)
  if (ctx.recentActivities.length > 0) {
    const actHVs = ctx.recentActivities.map(a => getHV(`activity:${a}`));
    components.push(bind(getHV("ROLE_ACTIVITY"), sequenceBind(actHVs)));
  }

  // Player mood
  if (ctx.playerMood) {
    components.push(bind(getHV("ROLE_PLAYER_MOOD"), getHV(`pmood:${ctx.playerMood}`)));
  }

  // Social density
  components.push(bind(getHV("ROLE_SOCIAL"), getHV(
    ctx.visitors === 0 ? "social:solitary" :
    ctx.visitors === 1 ? "social:intimate" :
    ctx.visitors < 4   ? "social:small_group" : "social:gathering"
  )));

  return components.length > 0 ? bundleHVs(components) : new Uint8Array(D);
}

// ─── Semantic Queries ────────────────────────────────────────────────────────

/** Scene-level query: how strongly is a concept associated with this room state? */
export function queryScene(roomHV: Uint8Array, concept: string): number {
  const conceptHV = getHV(concept);
  // Unbind the concept from the scene, compare to the null vector
  // High similarity to null = concept was not bound in
  // Low similarity to null = concept was bound in (result is structured)
  const unbound = query(roomHV, conceptHV);
  // Instead: directly check similarity between scene and concept
  return similarity(roomHV, conceptHV);
}

/**
 * Compare two room states: how similar are they?
 * Useful for the Aeterna detecting "this room feels different today"
 */
export function compareScenes(a: Uint8Array, b: Uint8Array): number {
  return similarity(a, b);
}

/**
 * Find the closest known concept to an unbound result.
 * Used after querying: unbind ROLE_MOOD from scene → what mood dimension dominates?
 */
export function findClosest(hv: Uint8Array, candidates: string[]): { concept: string; sim: number } | null {
  let best: { concept: string; sim: number } | null = null;
  for (const c of candidates) {
    const sim = similarity(hv, getHV(c));
    if (!best || sim > best.sim) best = { concept: c, sim };
  }
  return best;
}

// ─── Memory (scene history for drift detection) ──────────────────────────────

const SCENE_HISTORY_MAX = 32;
const sceneHistory: Uint8Array[] = [];

/** Record a scene snapshot for temporal comparison */
export function recordScene(sceneHV: Uint8Array): void {
  sceneHistory.push(new Uint8Array(sceneHV));
  if (sceneHistory.length > SCENE_HISTORY_MAX) sceneHistory.shift();
}

/** Get average similarity of current scene to recent history */
export function sceneDrift(currentHV: Uint8Array): number {
  if (sceneHistory.length === 0) return 0;
  let total = 0;
  for (const past of sceneHistory) total += similarity(currentHV, past);
  return 1 - total / sceneHistory.length; // 0 = no drift, 1 = completely different
}

// ─── Exports ─────────────────────────────────────────────────────────────────

export { D as HDC_DIM, SIMILARITY_THRESHOLD };
